/**
 * The one place this front end talks to the platform.
 *
 * Three properties worth stating, because each of them is a decision rather
 * than a default:
 *
 *   · credentials: 'same-origin'. The session is an httponly cookie the server
 *     set. This front end cannot read it, which is exactly why a cross-site
 *     script cannot carry it away. Nothing here stores a token.
 *
 *   · a refusal is a value, not an exception to swallow. The server answers a
 *     permission refusal with 403 and problem+json carrying the sentence that
 *     explains which role holds what, and a reason gate with 428. Both are
 *     answers the user should read, so ApiError keeps the status and the detail
 *     and the screens render them.
 *
 *   · X-FP-Front: react on every request. Write handlers answer a form post
 *     with a redirect, which is right for the server rendered tier; this header
 *     asks for the destination as JSON instead, so one set of handlers serves
 *     both front ends and neither has a copy of the other's rules.
 */
import type {
  AccessPayload, AuditPayload, Bootstrap, ExplainPayload, ListPayload,
  PagePayload, Problem,
} from './types'
import { staticApi } from './static'

const BASE = '/api/ui/v1'

export class ApiError extends Error {
  status: number
  detail: string
  title: string
  /** The body the server sent, when it sent a surface rather than a problem.
   *  A refusal and a reason gate are answers with their own pages, and the
   *  server renders them with the status that says so; discarding the body
   *  and rendering a generic refusal would throw away the sentence that
   *  explains which role holds what. */
  payload: unknown

  constructor(status: number, title: string, detail?: string | null,
              payload?: unknown) {
    super(detail || title)
    this.status = status
    this.title = title
    this.detail = detail || title
    this.payload = payload
  }

  /** A refusal of permission. The user may read why. */
  get denied() { return this.status === 403 }
  /** The section is held but a stated reason is recorded before it opens. */
  get needsReason() { return this.status === 428 }
  get unauthenticated() { return this.status === 401 }
}

async function parse(response: Response) {
  const text = await response.text()
  if (!text) return null
  try { return JSON.parse(text) } catch { return { raw: text } }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path.startsWith('/') ? path : `${BASE}/${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'X-FP-Front': 'react',
      Accept: 'application/json',
      ...(init.headers || {}),
    },
  })
  const body = await parse(response)
  if (!response.ok) {
    const problem = (body || {}) as Partial<Problem>
    throw new ApiError(response.status, problem.title || response.statusText,
                       problem.detail, body)
  }
  return body as T
}

function form(fields: Record<string, string | number | boolean | null | undefined>) {
  const body = new URLSearchParams()
  for (const [key, value] of Object.entries(fields)) {
    if (value === null || value === undefined) continue
    body.set(key, String(value))
  }
  return body
}

export const liveApi = {
  // ---- session --------------------------------------------------------
  access: () => request<AccessPayload>(`${BASE}/access`),

  signIn: (fields: { email: string; password: string; role_code: string; partner_code?: string }) =>
    request<{ ok: boolean; landing: string }>(`${BASE}/session`, {
      method: 'POST', body: form(fields),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }),

  signOut: () => request<{ ok: boolean }>(`${BASE}/session`, { method: 'DELETE' }),

  switchRole: (role_code: string) =>
    request<{ ok: boolean; landing: string }>(`${BASE}/session/role`, {
      method: 'POST', body: form({ role_code }),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }),

  setLocale: (code: string) =>
    request<{ ok: boolean; locale: string }>(`${BASE}/session/locale/${code}`),

  // ---- the shell ------------------------------------------------------
  bootstrap: () => request<Bootstrap>(`${BASE}/bootstrap`),

  // ---- surfaces -------------------------------------------------------
  /** Any /app path, resolved on the server exactly as the HTML tier resolves it. */
  page: (path: string, params?: Record<string, string>) => {
    const query = new URLSearchParams({ path, ...(params || {}) })
    return request<PagePayload>(`${BASE}/page?${query.toString()}`)
  },

  /** One list, for paging and filtering without re-resolving the route. */
  list: (sectionKey: string, params?: Record<string, string>) => {
    const query = new URLSearchParams(params || {})
    const suffix = query.toString() ? `?${query.toString()}` : ''
    return request<{ template: string; data: { d: ListPayload } }>(
      `${BASE}/list/${sectionKey}${suffix}`)
  },

  explain: (sectionKey: string) =>
    request<ExplainPayload>(`${BASE}/explain/${sectionKey}`),

  /** `link` is the audit path a row or a record carried, /app/audit/{t}/{k}. */
  audit: (link: string) => {
    const tail = link.replace(/^\/app\/audit\//, '')
    return request<AuditPayload>(`${BASE}/audit/${tail}`)
  },

  enumValues: (schema: string, name: string) =>
    request<{ type: string; values: string[] }>(`${BASE}/enum/${schema}/${name}`),

  health: () => request<Record<string, number>>(`${BASE}/health/ui`),

  /**
   * Take a write action.
   *
   * The path is the same one the server rendered form posts to, because it is
   * the same handler: the grant check, the four-eyes constraint, the outbox
   * write and the audit context are not reimplemented here. The answer is the
   * destination the handler chose, which the caller navigates to.
   */
  act: (path: string, fields: Record<string, string | number | boolean | null | undefined>) =>
    request<{ ok: boolean; redirect: string }>(path, {
      method: 'POST', body: form(fields),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }),
}


// ---------------------------------------------------------------------------
// Which client this build uses
// ---------------------------------------------------------------------------
/**
 * Two modes, chosen at build time.
 *
 * `live` talks to the platform. `static` answers from a snapshot exported out
 * of the platform and holds what you do in localStorage, so the build is files
 * on a static host with nothing running behind it.
 *
 * The switch is here and nowhere else. Every screen, every drawer and every
 * form calls `api`, and not one of them knows which mode it is in: a component
 * that branched on the mode would be a component that behaves differently in
 * the build people are shown from the build that goes live.
 */
export const MODE: 'live' | 'static' =
  (import.meta.env.VITE_FP_MODE as 'live' | 'static') || 'live'

export const IS_STATIC = MODE === 'static'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const api: typeof liveApi = (IS_STATIC
  ? (staticApi as unknown as typeof liveApi)
  : liveApi)
